// @flow
import last from 'lodash/fp/last';

import { type Facet } from 'modules/facet';
import { type Product, type Variation } from 'modules/product';
import { type SelectionMode } from 'ui/private/selection-table';

export type ProductsSourceType = 'document' | 'custom_catalog' | 'catalog';

export type RecipientStatuses =
  | 'disallowed'
  | 'bounced'
  | 'unsubscribed'
  | 'spamreport'
  | 'valid';

export type Selection = $ReadOnly<{|
  selected: $ReadOnlyArray<string>,
  deselected: $ReadOnlyArray<string>,
  mode: SelectionMode,
|}>;

export const defaultSelection: Selection = {
  mode: null,
  selected: [],
  deselected: [],
};

export type MailFormValues = {|
  replyToName: string | null,
  replyToEmail: string | null,
|};

// GET Campaign (after creation)
export type Campaign = $ReadOnly<{|
  _id: string,
  name: string,
  subject: string,
  thumbnailUrl: string,
  createdAt: string | Date,
  updatedAt: string | Date,
  createdBy: string,
  state: 'published' | 'draft',
  activeState: 'draft' | 'scheduled' | 'active' | 'closed',
  startTime: string | Date | null,
  endTime: string | Date | null,
  contactIds: $ReadOnlyArray<string> | null,
  groupIds: $ReadOnlyArray<string> | null,
  productSource: $ReadOnly<{
    source: ProductsSourceType,
    sourceId: string | null,
    name: string | null,
    catalogKey: string | null,
  }> | null,
  mail: {
    replyToName: string,
    replyToEmail: string,
    body: string,
    data: any,
  } | null,
  metrics: {
    amountPurchased: string,
    bounced: number,
    clicked: number,
    clickedRate: string,
    conversionRate: string,
    converted: number,
    delivered: number,
    opened: number,
    openedRate: string,
    ordersPlaced: number,
    recipients: number,
    sent: number,
    uniqueClicked: number,
    uniqueClickedRate: string,
    uniqueOpened: number,
    uniqueOpenedRate: string,
    unsubscribed: number,
  } | null,
|}>;

export type SerializedCampaign = $ReadOnly<{|
  _id: string,
  name: string,
  subject: string,
  thumbnail_url: string,
  created_at: string | Date,
  updated_at: string | Date,
  created_by: string,
  state: 'published' | 'draft',
  active_state: 'draft' | 'scheduled' | 'active' | 'closed',
  start_time: string | Date | null,
  end_time: string | Date | null,
  contact_ids: $ReadOnlyArray<string> | null,
  group_ids: $ReadOnlyArray<string> | null,
  product_source: $ReadOnly<{
    source: ProductsSourceType,
    source_id: string,
    name: string,
    catalog_key: string,
  }>,
  mail: {
    reply_to_name: string,
    reply_to_email: string,
    body: string,
    data: any,
  } | null,
  metrics?: {
    amount_purchased: string,
    bounced: number,
    clicked: number,
    clicked_rate: string,
    conversion_rate: string,
    converted: number,
    delivered: number,
    opened: number,
    opened_rate: string,
    orders_placed: number,
    recipients: number,
    sent: number,
    unique_clicked: number,
    unique_clicked_rate: string,
    unique_opened: number,
    unique_opened_rate: string,
    unsubscribed: number,
  },
|}>;

const deserializedCampaign = (campaign: SerializedCampaign) => ({
  // eslint-disable-next-line no-underscore-dangle
  _id: campaign._id,
  name: campaign.name,
  subject: campaign.subject,
  thumbnailUrl: campaign.thumbnail_url,
  createdAt: campaign.created_at,
  updatedAt: campaign.updated_at,
  createdBy: campaign.created_by,
  state: campaign.state,
  activeState: campaign.active_state,
  startTime: campaign.start_time,
  endTime: campaign.end_time,
  contactIds: campaign.contact_ids,
  groupIds: campaign.group_ids,
  productSource: campaign.product_source
    ? {
        source: campaign.product_source.source,
        sourceId: campaign.product_source.source_id,
        name: campaign.product_source.name,
        catalogKey: campaign.product_source.catalog_key,
      }
    : null,
  mail: campaign.mail
    ? {
        replyToName: campaign.mail.reply_to_name,
        replyToEmail: campaign.mail.reply_to_email,
        body: campaign.mail.body,
        data: campaign.mail.data,
      }
    : null,
  metrics: campaign.metrics
    ? {
        amountPurchased: campaign.metrics.amount_purchased,
        bounced: campaign.metrics.bounced,
        clicked: campaign.metrics.clicked,
        clickedRate: campaign.metrics.clicked_rate,
        conversionRate: campaign.metrics.conversion_rate,
        converted: campaign.metrics.converted,
        delivered: campaign.metrics.delivered,
        opened: campaign.metrics.opened,
        openedRate: campaign.metrics.opened_rate,
        ordersPlaced: campaign.metrics.orders_placed,
        recipients: campaign.metrics.recipients,
        sent: campaign.metrics.sent,
        uniqueClicked: campaign.metrics.unique_clicked,
        uniqueClickedRate: campaign.metrics.unique_clicked_rate,
        uniqueOpened: campaign.metrics.unique_opened,
        uniqueOpenedRate: campaign.metrics.unique_opened_rate,
        unsubscribed: campaign.metrics.unsubscribed,
      }
    : null,
});

export type CampaignDraft = $ReadOnly<{|
  // if we edit a campaign draft it will have an id
  // it will not though on first save of a new campaign
  _id?: string,
  name: string, // this is the only field required to be allowed to save a campaign draft
  subject: string | null,
  thumbnailUrl: string | null,
  state: 'published' | 'draft',
  startTime: string | Date | null,
  endTime: string | Date | null,
  productSource: $ReadOnly<{
    source: ProductsSourceType,
    sourceId: string | null,
    name: string | null,
    catalogKey: string | null,
  }> | null,
  mail: {
    replyToName: string | null,
    replyToEmail: string | null,
    body: string | null,
    data: any,
  } | null,
  contactIds: $ReadOnlyArray<string> | null,
  groupIds: $ReadOnlyArray<string> | null,
  contact_selection?: {
    contacts: { keyword: string | null } | null,
    groups: { keyword: string | null } | null,
  },
|}>;

export type SerializedCampaignDraft = $ReadOnly<{|
  _id?: string,
  name: string,
  subject: string | null,
  thumbnail_url: string | null,
  state: 'published' | 'draft',
  start_time: string | Date | null,
  end_time: string | Date | null,
  product_source: $ReadOnly<{
    source: ProductsSourceType,
    source_id: string | null,
    name: string | null,
    catalog_key: string | null,
  }> | null,
  mail: {
    reply_to_name: string | null,
    reply_to_email: string | null,
    body: string | null,
    data: any,
  } | null,
  contact_ids: $ReadOnlyArray<string> | null,
  group_ids: $ReadOnlyArray<string> | null,
  contact_selection?: {
    contacts: { keyword: string | null } | null,
    groups: { keyword: string | null } | null,
  },
|}>;

// for POST
export const serializedCampaignDraft = (
  campaignDraft: CampaignDraft,
): SerializedCampaignDraft => ({
  name: campaignDraft.name,
  subject: campaignDraft.subject,
  thumbnail_url: campaignDraft.thumbnailUrl,
  state: campaignDraft.state,
  start_time: campaignDraft.startTime,
  end_time: campaignDraft.endTime,
  product_source: campaignDraft.productSource
    ? {
        source: campaignDraft.productSource.source,
        source_id: campaignDraft.productSource.sourceId,
        name: campaignDraft.productSource.name,
        catalog_key: campaignDraft.productSource.catalogKey,
      }
    : null,
  mail: campaignDraft.mail
    ? {
        reply_to_name: campaignDraft.mail.replyToName,
        reply_to_email: campaignDraft.mail.replyToEmail,
        body: campaignDraft.mail.body,
        data: campaignDraft.mail.data,
      }
    : null,
  contact_ids: campaignDraft.contactIds,
  group_ids: campaignDraft.groupIds,
  contact_selection: campaignDraft.contact_selection,
});

export const serializedEditedCampaign = (
  editedCampaign: CampaignDraft,
): SerializedCampaignDraft => ({
  // eslint-disable-next-line no-underscore-dangle
  _id: editedCampaign._id,
  name: editedCampaign.name,
  subject: editedCampaign.subject,
  thumbnail_url: editedCampaign.thumbnailUrl,
  state: editedCampaign.state,
  start_time: editedCampaign.startTime,
  end_time: editedCampaign.endTime,
  product_source: editedCampaign.productSource
    ? {
        source: editedCampaign.productSource.source,
        source_id: editedCampaign.productSource.sourceId,
        name: editedCampaign.productSource.name,
        catalog_key: editedCampaign.productSource.catalogKey,
      }
    : null,
  mail: editedCampaign.mail
    ? {
        reply_to_name: editedCampaign.mail.replyToName,
        reply_to_email: editedCampaign.mail.replyToEmail,
        body: editedCampaign.mail.body,
        data: editedCampaign.mail.data,
      }
    : null,
  contact_ids: editedCampaign.contactIds,
  group_ids: editedCampaign.groupIds,
  contact_selection: editedCampaign.contact_selection,
});

export const deserializedEditedCampaign = (
  editedCampaign: SerializedCampaignDraft,
): CampaignDraft => ({
  // eslint-disable-next-line no-underscore-dangle
  _id: editedCampaign._id,
  name: editedCampaign.name,
  subject: editedCampaign.subject,
  thumbnailUrl: editedCampaign.thumbnail_url,
  state: editedCampaign.state,
  startTime: editedCampaign.start_time,
  endTime: editedCampaign.end_time,
  productSource: editedCampaign.product_source
    ? {
        source: editedCampaign.product_source.source,
        sourceId: editedCampaign.product_source.source_id,
        name: editedCampaign.product_source.name,
        catalogKey: editedCampaign.product_source.catalog_key,
      }
    : null,
  mail: editedCampaign.mail
    ? {
        replyToName: editedCampaign.mail.reply_to_name,
        replyToEmail: editedCampaign.mail.reply_to_email,
        body: editedCampaign.mail.body,
        data: editedCampaign.mail.data,
      }
    : null,
  contactIds: editedCampaign.contact_ids,
  groupIds: editedCampaign.group_ids,
});

export type CampaignsQuery = $ReadOnly<{|
  Range?: string,
  sortBy?:
    | 'name'
    | 'delivered'
    | 'uniqueOpenedRate'
    | 'uniqueClickedRate'
    | 'conversionRate'
    | 'amountPurchased'
    | 'state',
  sortDirection?: 'ASC' | 'DESC',
  keyword?: string | null,
|}>;

export type SerializedCampaignsQueryResponse = $ReadOnly<{|
  success: boolean,
  data: $ReadOnlyArray<SerializedCampaign>,
  range: string,
  errors?: $ReadOnlyArray<
    $ReadOnly<{|
      message: string,
      code: string,
    |}>,
  >,
|}>;
export type CampaignsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Campaign>,
  total: number,
|}>;

export const campaignsQueryAsQueryString = ({
  Range,
  sortBy,
  sortDirection,
  keyword,
}: CampaignsQuery): string => {
  const params = new URLSearchParams();

  if (Range) params.append('Range', Range);

  if (keyword) params.append('keyword', keyword);

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const serializedSortByMap: $ReadOnly<{ [string]: string }> = {
    delivered: 'metrics.delivered',
    uniqueOpenedRate: 'metrics.unique_opened_rate',
    uniqueClickedRate: 'metrics.unique_clicked_rate',
    conversionRate: 'metrics.conversion_rate',
    amountPurchased: 'metrics.amount_purchased',
  };

  const sortParam = encodeURIComponent(
    sortBy
      ? `sort(${sortDirectionSymbol}${serializedSortByMap[sortBy] || sortBy})`
      : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export type DocumentsQuery = $ReadOnly<{|
  Range?: string,
  state: string,
  createdOnStart: string,
  customer?: string,
|}>;

export const documentsQueryAsQueryString = ({
  Range,
  state,
  createdOnStart,
  customer,
}: DocumentsQuery): string => {
  const params = new URLSearchParams();

  if (Range) params.append('Range', Range);

  if (state) params.append('state', state);

  if (createdOnStart) params.append('created_on_start', createdOnStart);

  if (customer) params.append('customer_number', customer);

  return `${params.toString()}`;
};

export const deserializedCampaignsQueryResponse = ({
  data,
  range,
}: SerializedCampaignsQueryResponse): CampaignsQueryResponse => ({
  results: data.map(deserializedCampaign),
  total: Number(last(range.split('/'))),
});

export type CampaignQuery = $ReadOnly<{|
  id: string,
|}>;

export type CampaignQueryResponse = $ReadOnly<{|
  results: Campaign,
|}>;

export type SerializedCampaignQueryResponse = $ReadOnly<{|
  results: SerializedCampaign,
|}>;

export const deserializedCampaignQueryResponse = ({
  results,
}: SerializedCampaignQueryResponse): CampaignQueryResponse => ({
  results: deserializedCampaign(results),
});

// not sure if i should use users from modules/users or if this will be diff
export type Contact = $ReadOnly<{|
  _id: string,
  name: string,
  email: string,
  company: string,
  groups: $ReadOnlyArray<string>,
  last_sent_at: Date | string,
  address: {
    city: string | null,
    country: string | null,
    state: string | null,
    street1: string | null,
    street2: string | null,
    zip: string | null,
  } | null,
  status: RecipientStatuses,
|}>;

export type SerializedContact = $ReadOnly<{|
  ...Contact,
|}>;

export type ContactsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Contact>,
  total: number,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type SerializedContactsQueryResponse = $ReadOnly<{|
  success: boolean,
  data: $ReadOnly<{|
    results: $ReadOnlyArray<SerializedContact>,
    facets: $ReadOnlyArray<Facet>,
  |}>,
  range: string,
|}>;

export type ContactsQuery = $ReadOnly<{|
  Range?: string,
  sortBy?: 'name' | 'company' | 'location' | 'last_sent_at',
  sortDirection?: 'ASC' | 'DESC',
  keyword?: string | null,
  lastUpdated?: Date,
  selection?: Selection | null,
  rep2repsharing?: boolean,
|}>;

const paramsForSelectableQuery = ({
  Range,
  keyword,
  selection,
  rep2repsharing,
}: {|
  Range?: string,
  keyword?: string | null,
  selection?: Selection | null,
  rep2repsharing?: boolean,
|}): URLSearchParams => {
  const params = new URLSearchParams();

  if (Range) params.append('Range', Range);

  if (keyword) params.append('keyword', keyword);

  if (rep2repsharing) params.append('rep2repsharing', String(rep2repsharing));

  if (selection) {
    const key = selection.mode === 'all' ? 'excludes[]' : 'includes[]';

    selection.selected.forEach((id) => {
      params.append(key, id);
    });
  }

  return params;
};

export const contactsQueryAsQueryString = ({
  Range,
  sortBy,
  sortDirection,
  keyword,
  selection,
  rep2repsharing,
}: ContactsQuery): string => {
  const params = paramsForSelectableQuery({
    Range,
    keyword,
    selection,
    rep2repsharing,
  });

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const serializedSortByMap: $ReadOnly<{ [string]: string }> = {
    location: 'address.billing_address',
  };

  const sortParam = encodeURIComponent(
    sortBy
      ? `sort(${sortDirectionSymbol}${serializedSortByMap[sortBy] || sortBy})`
      : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export const deserializedContactsQueryResponse = ({
  data: { results, facets },
  range,
}: SerializedContactsQueryResponse): ContactsQueryResponse => ({
  results,
  facets,
  total: Number(last(range.split('/'))),
});

// how different is this from modules/users groups?
// will they be the same thing?
export type Group = $ReadOnly<{|
  _id: string,
  name: string,
  last_sent_at: Date | string | null,
  contact_ids: $ReadOnlyArray<string>,
|}>;

export type GroupResponse = $ReadOnly<{|
  success: boolean,
  data: Group,
|}>;

export type SerializedGroup = $ReadOnly<{|
  ...Group,
|}>;

export type SerializedGroupsQueryResponse = $ReadOnly<{|
  success: boolean,
  data: $ReadOnlyArray<SerializedGroup>,
  range: string,
|}>;

export type GroupsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Group>,
  total: number,
|}>;

export type GroupsQuery = $ReadOnly<{|
  Range?: string,
  sortBy?: 'name' | 'contact_ids' | 'last_sent_at',
  sortDirection?: 'ASC' | 'DESC',
  keyword?: string | null,
  lastUpdated?: Date,
  selection?: Selection | null,
  rep2repsharing?: boolean,
|}>;

export const groupsQueryAsQueryString = ({
  Range,
  sortBy,
  sortDirection,
  keyword,
  selection,
  rep2repsharing,
}: GroupsQuery): string => {
  const params = paramsForSelectableQuery({
    Range,
    keyword,
    selection,
    rep2repsharing,
  });
  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const sortParam = encodeURIComponent(
    sortBy ? `sort(${sortDirectionSymbol}${sortBy})` : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export const deserializedGroupsQueryResponse = ({
  data,
  range,
}: SerializedGroupsQueryResponse): GroupsQueryResponse => ({
  results: data,
  total: Number(last(range.split('/'))),
});

export type SourceOfFeaturedProducts = $ReadOnly<{
  sourceId: string | null,
  name: string | null,
  source: ProductsSourceType,
  catalogKey: string | null,
}>;

export type SerializedSourceOfFeaturedProducts = $ReadOnly<{
  source_id: string | null,
  name: string | null,
  source: ProductsSourceType | null,
  catalog_key: string | null,
}>;

export type VariationsQueryResponseForCampaigns = $ReadOnly<{
  results: $ReadOnly<{
    products: $ReadOnlyArray<Product>,
    variations: $ReadOnlyArray<Variation>,
  }>,
}>;

export type VariationsQuery = $ReadOnly<{|
  Range?: string,
  catalogKey: string,
  customCatalogId?: string,
  orderId?: string,
  variations?: boolean,
|}>;

export const variationsForCampaignsQueryAsQueryString = ({
  Range,
  catalogKey,
  customCatalogId,
  orderId,
  variations,
}: VariationsQuery): string => {
  const params = new URLSearchParams();

  if (Range) params.append('Range', Range);

  if (catalogKey) params.append('catalog', catalogKey);

  if (customCatalogId) params.append('custom_catalog', customCatalogId);

  if (orderId) params.append('document_id', orderId);

  if (variations) params.append('variations', 'true');

  return `${params.toString()}`;
};

// for POST
export const serializedSource = (
  source: SourceOfFeaturedProducts,
): SerializedSourceOfFeaturedProducts => ({
  source: source.source,
  source_id: source.sourceId,
  name: source.name,
  catalog_key: source.catalogKey,
});

export type CampaignRecipient = $ReadOnly<{|
  _id: string,
  name: string,
  email: string,
  company: string,
  address: {
    street1: string,
    city: string,
    state: string,
    zip: string,
    country: string,
  },
  metrics: {
    amount_purchased: string,
    bounced: number,
    clicked: number,
    clicked_rate: string,
    conversion_rate: string,
    converted: number,
    delivered: number,
    opened: number,
    opened_rate: string,
    orders_placed: number,
    sent: number,
    unique_clicked: number,
    unique_clicked_rate: string,
    unique_opened: number,
    unique_opened_rate: string,
  },
  status: RecipientStatuses,
|}>;

export type SerializedCampaignRecipient = $ReadOnly<{|
  ...CampaignRecipient,
|}>;

export type CampaignRecipientsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Group>,
  total: number,
|}>;

export type SerializedCampaignRecipientsQueryResponse = $ReadOnly<{|
  data: $ReadOnlyArray<Group>,
  range: string,
|}>;

export type CampaignRecipientsQuery = $ReadOnly<{|
  Range?: string,
  sortBy?:
    | 'email'
    | 'company'
    | 'location'
    | 'unique_opened'
    | 'unique_clicked'
    | 'converted'
    | 'amount_purchased',
  sortDirection?: 'ASC' | 'DESC',
  keyword?: string | null,
  id: string,
|}>;

export const campaignRecipientsQueryQueryAsQueryString = ({
  Range,
  sortBy,
  sortDirection,
  keyword,
}: CampaignRecipientsQuery): string => {
  const params = new URLSearchParams();

  if (Range) params.append('Range', Range);

  if (keyword) params.append('keyword', keyword);

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const serializedSortByMap: $ReadOnly<{ [string]: string }> = {
    billing_address: 'address.billing_address',
    unique_opened: 'metrics.unique_opened',
    unique_clicked: 'metrics.unique_clicked',
    converted: 'metrics.converted',
    amount_purchased: 'metrics.amount_purchased',
  };

  const sortParam = encodeURIComponent(
    sortBy
      ? `sort(${sortDirectionSymbol}${serializedSortByMap[sortBy] || sortBy})`
      : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export const deserializedCampaignRecipientsQueryResponse = ({
  data,
  range,
}: SerializedCampaignRecipientsQueryResponse): CampaignRecipientsQueryResponse => ({
  results: data,
  total: Number(last(range.split('/'))),
});

export type GroupQueryResponse = $ReadOnly<{|
  results: $ReadOnly<{|
    _id?: string,
    name: string,
    contacts: $ReadOnlyArray<string>,
  |}>,
|}>;

export const newCampaignTemplate = (name: string): CampaignDraft => ({
  name,
  subject: null,
  thumbnailUrl: null,
  state: 'draft',
  startTime: null,
  endTime: null,
  productSource: null,
  mail: {
    replyToName: '',
    replyToEmail: '',
    body: null,
    data: null,
  },
  contactIds: null,
  groupIds: null,
  contact_selection: {
    groups: null,
    contacts: null,
  },
});

export const campaignAsCampaignDraft = (
  campaign: Campaign | CampaignDraft,
): any => {
  // eslint-disable-next-line no-unused-vars, camelcase
  const { _id, contact_selection, ...campaignBluePrint } =
    newCampaignTemplate(''); // eslint-disable-line
  return { ...campaignBluePrint, ...campaign };
};

// if a email/recipient has this status, they cannot be sent emails
export const badRecipientStatuses: ['bounced', 'unsubscribed', 'spamreport'] = [
  'bounced',
  'unsubscribed',
  'spamreport',
];

export const recipientStatusPillColor = (status: RecipientStatuses): string => {
  switch (status) {
    case 'bounced':
      return 'red';
    case 'spamreport':
      return 'orange';
    case 'disallowed':
    case 'unsubscribed':
    default:
      return 'black';
  }
};

export const recipientStatusPillText = (
  status: RecipientStatuses | null,
  showDisallowed?: boolean,
): string | null => {
  switch (status) {
    case 'bounced':
      return 'bounced';
    case 'spamreport':
      return 'reported_as_spam';
    case 'disallowed':
      return showDisallowed ? 'not_sent' : null;
    case 'unsubscribed':
      return 'unsubscribed';
    case null:
    default:
      return null;
  }
};

export type CampaignSendTestEmailResponse = $ReadOnly<{|
  result: any,
|}>;

export type SerializedCampaignSendTestEmailResponse = $ReadOnly<{|
  data: any,
|}>;

export type CampaignSendTestEmailQuery = $ReadOnly<{|
  id: string,
  to: string,
|}>;

export const campaignSendTestEmailQueryAsQueryString = ({
  to,
}: CampaignSendTestEmailQuery): string => {
  const params = new URLSearchParams();

  if (to) params.append('to', to);

  return params.toString();
};
